import { useMemo } from "react";
import { buildLayout, DEMO_PROJECT, getSheetSize } from "@plaatplan/core";
import { interpolate } from "@plaatplan/i18n";
import { useGuideSketches } from "../../i18n/useVisualGuides";

function rgba(hex, alpha) {
  const normalized = hex.replace("#", "");
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function pickPreviewSheet(sheets) {
  return [...sheets]
    .filter((sheet) => !sheet.unused && sheet.placements.length > 0)
    .sort((a, b) => b.placements.length - a.placements.length)[0] ?? sheets[0];
}

export function SketchMaterialCard() {
  const s = useGuideSketches();

  return (
    <div className="guide-sketch guide-sketch--card">
      <p className="guide-sketch-label">{s.materialStock}</p>
      <div className="guide-sketch-block">
        <span className="guide-sketch-chip guide-sketch-chip--on">{s.materialMdfExample}</span>
        <span className="guide-sketch-chip">{s.materialMultiplex}</span>
      </div>
      <div className="guide-sketch-fields">
        <div><em>{s.length}</em><strong>2440</strong></div>
        <div><em>{s.width}</em><strong>1220</strong></div>
        <div className="guide-sketch-highlight"><em>{s.kerf}</em><strong>2,2 mm</strong></div>
      </div>
      <div className="guide-sketch-rest">
        <span>{s.addOffcut}</span>
      </div>
    </div>
  );
}

export function SketchProjectInput() {
  const s = useGuideSketches();

  return (
    <div className="guide-sketch guide-sketch--card">
      <p className="guide-sketch-label">{s.subprojectExample}</p>
      <div className="guide-sketch-input">
        600×1220×2 <span>{s.examplePartLineSidePanel}</span>
      </div>
      <table className="guide-sketch-table">
        <thead>
          <tr>
            <th>{s.panel}</th>
            <th>{s.lengthByWidth}</th>
            <th>{s.quantityShort}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><i className="guide-sketch-dot" style={{ background: "#64a950" }} />{s.sidePanel}</td>
            <td>600×1220</td>
            <td>2</td>
          </tr>
          <tr>
            <td><i className="guide-sketch-dot" style={{ background: "#c77842" }} />{s.shelf}</td>
            <td>564×380</td>
            <td>2</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function SketchBoardAndResult() {
  const s = useGuideSketches();
  const { sheet, sourcePanel, metrics } = useMemo(() => {
    const layout = buildLayout(DEMO_PROJECT.sourcePanel, DEMO_PROJECT.projects);
    const picked = pickPreviewSheet(layout.sheets);
    return { sheet: picked, sourcePanel: DEMO_PROJECT.sourcePanel, metrics: layout.metrics };
  }, []);

  const { length, width } = getSheetSize(sheet, sourcePanel);
  const scale = 0.11;

  return (
    <div className="guide-sketch guide-sketch--split">
      <div className="guide-sketch-board-pane">
        <p className="guide-sketch-label">{s.boardLayoutSheet1}</p>
        <div
          className="board guide-sketch-board"
          style={{ width: `${length * scale}px`, height: `${width * scale}px` }}
        >
          {sheet.placements.slice(0, 6).map((placement) => (
            <div
              key={`${placement.panelId}-${placement.occurrence}`}
              className="placed-panel guide-sketch-piece"
              style={{
                left: `${placement.x * scale}px`,
                top: `${placement.y * scale}px`,
                width: `${placement.length * scale}px`,
                height: `${placement.width * scale}px`,
                borderColor: placement.projectColor,
                background: rgba(placement.projectColor, 0.22),
              }}
            />
          ))}
        </div>
      </div>
      <div className="guide-sketch-result-pane">
        <p className="guide-sketch-label">{s.result}</p>
        <div className="guide-sketch-stat"><strong>{metrics.sheetCount}</strong><span>{s.sheets}</span></div>
        <div className="guide-sketch-meter"><span style={{ width: `${Math.min(metrics.utilisation, 100)}%` }} /></div>
        <div className="guide-sketch-stat-small">
          {interpolate(s.utilisation, { percent: Math.round(metrics.utilisation) })}
        </div>
      </div>
    </div>
  );
}

export function SketchExportMenu({ activeItem = "pdf" }) {
  const s = useGuideSketches();
  const items = [
    { id: "pdf", label: s.exportWerkplanLabel, hint: s.exportWerkplanHint },
    { id: "material", label: s.exportMaterialLabel, hint: s.exportMaterialHint },
    { id: "csv", label: s.exportCsvLabel, hint: s.exportCsvHint },
  ];

  return (
    <div className="guide-sketch guide-sketch--toolbar">
      <div className="guide-sketch-header-bar">
        <span className="guide-sketch-logo">{s.brandName}</span>
        <span className="guide-sketch-btn guide-sketch-btn--ghost">{s.import}</span>
        <span className="guide-sketch-btn guide-sketch-btn--primary">{s.exportMenu}</span>
      </div>
      <div className="guide-sketch-menu">
        {items.map((item) => (
          <div
            key={item.id}
            className={`guide-sketch-menu-item${item.id === activeItem ? " guide-sketch-menu-item--active" : ""}`}
          >
            <strong>{item.label}</strong>
            <span>{item.hint}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SketchWerkplanTabBar({ activeTab = "prepare" }) {
  const s = useGuideSketches();
  const tabs = [
    { id: "prepare", step: "1", label: s.prepare },
    { id: "shop", step: "2", label: s.shopOrder },
  ];

  return (
    <div className="guide-sketch-tab-shell">
      <div className="guide-sketch-tab-bar" role="tablist" aria-label={s.werkplanStepsAriaLabel}>
        {tabs.map((tab) => (
          <span
            key={tab.id}
            className={`guide-sketch-tab${activeTab === tab.id ? " guide-sketch-tab--active" : ""}`}
          >
            <span className="guide-sketch-tab-step">{tab.step}</span>
            <span>{tab.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function SketchWerkplanSettings() {
  const s = useGuideSketches();

  return (
    <div className="guide-sketch guide-sketch--card">
      <SketchWerkplanTabBar activeTab="prepare" />
      <p className="guide-sketch-label">{s.whatToBring}</p>
      <div className="guide-sketch-toggle-row">
        <span className="guide-sketch-toggle guide-sketch-toggle--on">{s.newStandardSheets}</span>
        <span className="guide-sketch-toggle guide-sketch-toggle--on">{s.offcutMaterial}</span>
      </div>
      <div className="guide-sketch-select">
        <span>{s.cutStrategy}</span>
        <strong>{s.efficientCutting}</strong>
      </div>
      <label className="guide-sketch-checkbox">
        <span className="guide-sketch-checkbox-box guide-sketch-checkbox-box--on" aria-hidden="true" />
        {s.showCutLines}
      </label>
      <table className="guide-sketch-table guide-sketch-table--compact">
        <thead>
          <tr>
            <th>{s.panel}</th>
            <th>{s.length}</th>
            <th>{s.width}</th>
            <th>{s.quantity}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{s.sidePanel}</td>
            <td>600</td>
            <td>1220</td>
            <td>2</td>
          </tr>
          <tr>
            <td>{s.topPanel}</td>
            <td>1800</td>
            <td>350</td>
            <td>2</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function SketchSimulator() {
  const s = useGuideSketches();

  return (
    <div className="guide-sketch guide-sketch--card">
      <div className="guide-sketch-sim-teaser">
        <strong>{s.checkCutSequence}</strong>
        <span className="guide-sketch-btn guide-sketch-btn--ghost guide-sketch-btn--small">{s.viewSequence}</span>
      </div>
      <div className="guide-sketch-sim-board">
        <span className="guide-sketch-sim-cut guide-sketch-sim-cut--past" />
        <span className="guide-sketch-sim-cut guide-sketch-sim-cut--current" />
        <span className="guide-sketch-sim-piece" />
      </div>
      <p className="guide-sketch-sim-collapsed-hint">{s.simulatorCollapsedHint}</p>
    </div>
  );
}

export function SketchSimulatorOpen() {
  const s = useGuideSketches();

  return (
    <div className="guide-sketch guide-sketch--card guide-sketch--sim-open">
      <p className="guide-sketch-label">
        {interpolate(s.cutSequenceTitle, { current: 2, total: 5 })}
      </p>
      <div className="guide-sketch-sim-board guide-sketch-sim-board--open">
        <span className="guide-sketch-sim-dim" />
        <span className="guide-sketch-sim-cut guide-sketch-sim-cut--past" />
        <span className="guide-sketch-sim-cut guide-sketch-sim-cut--current" />
        <span className="guide-sketch-sim-piece guide-sketch-sim-piece--active" />
      </div>
      <p className="guide-sketch-sim-instruction">
        {interpolate(s.cutInstruction, { current: 2 })}
      </p>
      <div className="guide-sketch-sim-controls">
        <span className="guide-sketch-btn guide-sketch-btn--ghost guide-sketch-btn--small">{s.previous}</span>
        <span className="guide-sketch-sim-slider" aria-hidden="true"><span /></span>
        <span className="guide-sketch-btn guide-sketch-btn--ghost guide-sketch-btn--small">{s.next}</span>
      </div>
    </div>
  );
}

export function SketchSimulatorKeyboard() {
  const s = useGuideSketches();

  return (
    <div className="guide-sketch guide-sketch--card">
      <p className="guide-sketch-label">{s.keyboard}</p>
      <div className="guide-sketch-key-row">
        <span className="guide-sketch-key">{s.keyArrowLeft}</span>
        <span className="guide-sketch-key">{s.keyArrowRight}</span>
        <span className="guide-sketch-key">{s.keyEscape}</span>
      </div>
      <ul className="guide-sketch-key-legend">
        <li><strong>{s.keyArrowLeft} {s.keyArrowRight}</strong> {s.keyboardLegendArrows}</li>
        <li><strong>{s.keyEscape}</strong> {s.keyboardLegendEscape}</li>
      </ul>
    </div>
  );
}

export function SketchWerkplanShop() {
  const s = useGuideSketches();

  return (
    <div className="guide-sketch guide-sketch--card">
      <SketchWerkplanTabBar activeTab="shop" />
      <p className="guide-sketch-label">{s.shopOrderMaterial}</p>
      <div className="guide-sketch-print-lines guide-sketch-print-lines--shop">
        <span /><span /><span />
      </div>
      <p className="guide-sketch-shop-note">{s.shopNote}</p>
      <div className="guide-sketch-print-board guide-sketch-print-board--small" />
    </div>
  );
}

export function SketchPrintDialog() {
  const s = useGuideSketches();

  return (
    <div className="guide-sketch guide-sketch--print">
      <div className="guide-sketch-print-chrome">
        <span>{s.print}</span>
        <span className="guide-sketch-btn guide-sketch-btn--primary guide-sketch-btn--small">{s.saveAsPdf}</span>
      </div>
      <div className="guide-sketch-print-page">
        <p>{s.cutListAndWerkplan}</p>
        <div className="guide-sketch-print-lines">
          <span /><span /><span /><span />
        </div>
        <div className="guide-sketch-print-board" />
      </div>
    </div>
  );
}
